import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box } from '@mui/material';
import LangEnum from '../../../../../lang/LangEnum';
import OrderBillingForecastPreOrderType from '../../../../orders/enums/order-billing-forecast/order-billing-forecast-pre-order-type.enum';
import OrderBillingForecastReadyForDeliveryType from '../../../../orders/enums/order-billing-forecast/order-billing-forecast-ready-for-delivery-type.enum';
import { useTranslation } from '../../../../translation/TranslationContext';
import SettingContainer from '../../../SettingContainer';
import {
  getOrderBillingForecastPreOrderTypeEnum,
  getOrderBillingForecastReadyForDeliveryTypeEnum,
} from '../../../utils/getOrderBillingForecastRuleLangEnum.utils';
import { OrderBillingForecastSectionProps } from '../../types/GeneralSettings.types';
import useOrderBillingForecast from './hooks/useOrderBillingForecast';

const OrderBillingForecastSection = ({
  generalSettings,
  handleChangeAndBlur,
}: OrderBillingForecastSectionProps) => {
  const { translate } = useTranslation();

  const {
    readyForDeliveryDays,
    preOrderDays,
    handleReadyForDeliveryTypeChange,
    handleReadyForDeliveryDaysChange,
    handleReadyForDeliveryDaysBlur,
    handlePreOrderTypeChange,
    handlePreOrderDaysChange,
    handlePreOrderDaysBlur,
  } = useOrderBillingForecast({ generalSettings, handleChangeAndBlur });

  const readyForDeliveryItems = Object.keys(OrderBillingForecastReadyForDeliveryType).map(
    (type) => ({
      value: type,
      label: translate(
        getOrderBillingForecastReadyForDeliveryTypeEnum(
          type as OrderBillingForecastReadyForDeliveryType,
        ),
      ),
    }),
  );

  const preOrderItems = Object.keys(OrderBillingForecastPreOrderType).map((type) => ({
    value: type,
    label: translate(
      getOrderBillingForecastPreOrderTypeEnum(type as OrderBillingForecastPreOrderType),
    ),
  }));

  const readyForDeliveryValue =
    generalSettings.billingForecastRuleForReadyForDelivery?.billingForecastReadyForDeliveryType;
  const preOrderValue = generalSettings.billingForecastRuleForPreOrder?.billingForecastPreOrderType;

  return (
    <SettingContainer
      title={translate(LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_TITLE)}
      description={LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_DESCRIPTION}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Select
            displayEmpty
            label={translate(
              LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_RULE_FOR_READY_FOR_DELIVERY,
            )}
            name="billingForecastRuleForReadyForDelivery"
            value={readyForDeliveryValue ?? ''}
            renderValue={(selected) =>
              selected
                ? (readyForDeliveryItems.find((item) => item.value === selected)?.label ?? '')
                : translate(LangEnum.GLOBAL_DISABLE)
            }
            onChange={handleReadyForDeliveryTypeChange}
          >
            <MenuItem value="">{translate(LangEnum.GLOBAL_DISABLE)}</MenuItem>
            {readyForDeliveryItems.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>

          {readyForDeliveryValue ===
            OrderBillingForecastReadyForDeliveryType.CALCULATED_FROM_ORDER_DATE && (
            <Tooltip
              placement="bottom"
              TooltipContent={translate(
                LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_NUMBER_OF_DAYS_TOOLTIP,
              )}
            >
              <TextField
                label={translate(
                  LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_NUMBER_OF_DAYS_LABEL,
                )}
                name="billingForecastRuleForReadyForDelivery"
                value={readyForDeliveryDays ?? ''}
                slotProps={{ htmlInput: { min: 0, inputMode: 'numeric', pattern: '[0-9]*' } }}
                onBlur={handleReadyForDeliveryDaysBlur}
                onChange={handleReadyForDeliveryDaysChange}
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Select
            displayEmpty
            label={translate(LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_RULE_FOR_PRE_ORDER)}
            name="billingForecastRuleForPreOrder"
            value={preOrderValue ?? ''}
            renderValue={(selected) =>
              selected
                ? (preOrderItems.find((item) => item.value === selected)?.label ?? '')
                : translate(LangEnum.GLOBAL_DISABLE)
            }
            onChange={handlePreOrderTypeChange}
          >
            <MenuItem value="">{translate(LangEnum.GLOBAL_DISABLE)}</MenuItem>
            {preOrderItems.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>

          {preOrderValue === OrderBillingForecastPreOrderType.CALCULATED_FROM_ORDER_DATE && (
            <Tooltip
              placement="bottom"
              TooltipContent={translate(
                LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_NUMBER_OF_DAYS_TOOLTIP,
              )}
            >
              <TextField
                label={translate(
                  LangEnum.GENERAL_SETTINGS_ORDER_BILLING_FORECAST_NUMBER_OF_DAYS_LABEL,
                )}
                name="billingForecastRuleForPreOrder"
                value={preOrderDays ?? ''}
                slotProps={{ htmlInput: { min: 0, inputMode: 'numeric', pattern: '[0-9]*' } }}
                onBlur={handlePreOrderDaysBlur}
                onChange={handlePreOrderDaysChange}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
    </SettingContainer>
  );
};

export default OrderBillingForecastSection;
